import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { authApi } from '@/api/endpoints/auth'
import { subscriptionsApi, type SubscriptionStatusResponse } from '@/api/endpoints/subscriptions'
import ParishSetupModal from '@/components/parish/ParishSetupModal'
import TOSModal from '@/components/common/TOSModal'
import AppHeader from '@/components/common/AppHeader'
import RegistrationBlock from '@/components/dashboard/RegistrationBlock'
import { parishesApi } from '@/api/endpoints/parishes'
import AdminList from '@/components/dashboard/AdminList'
import TeacherList from '@/components/dashboard/TeacherList'
import ApprovalsAccordion from '@/components/dashboard/ApprovalsAccordion'
import NoticeBanner from '@/components/dashboard/NoticeBanner'
import StudentsList from '@/components/dashboard/StudentsList'
import ClassesList from '@/components/dashboard/ClassesList'
import AutoRegistrationRules from '@/components/dashboard/AutoRegistrationRules'
import MessageCenter from '@/components/messaging/MessageCenter'
import BirthdayReminders from '@/components/dashboard/BirthdayReminders'
import MergeDoubleParentTool from '@/components/dashboard/MergeDoubleParentTool'
import PasswordReminderBanner from '@/components/dashboard/PasswordReminderBanner'
import Notes from '@/components/dashboard/Notes'
import AppFooter from '@/components/common/AppFooter'
import RegistrationPdfModal from '@/components/dashboard/modals/RegistrationPdfModal'
import { isAdminRole } from '@/utils/roles'

function DashboardPage() {
  const { user, updateUser } = useAuth()
  const [showParishSetup, setShowParishSetup] = useState(false)
  const [showTos, setShowTos] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null)
  const [parishName, setParishName] = useState<string | undefined>(undefined)
  const [sharecode, setSharecode] = useState<string | undefined>(undefined)
  const [parishListRefreshKey, setParishListRefreshKey] = useState(0)
  const [showPdfModal, setShowPdfModal] = useState(false)
  // Dashboard accordion: only one section open at a time; all collapsed by default.
  const [openSection, setOpenSection] = useState<string | null>(null)
  const sectionProps = (id: string) => ({
    open: openSection === id,
    onToggle: () => setOpenSection((prev) => (prev === id ? null : id)),
  })

  useEffect(() => {
    // Fetch current user data on mount
    const fetchUser = async () => {
      try {
        const me = await authApi.getMe()
        updateUser(me)
        if (!me.tos_accepted_at) {
          setShowTos(true)
          return
        }
        if (me.role === 'PRIMARY_ADMIN' && !me.parish_id) {
          setShowParishSetup(true)
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error)
      }
    }
    fetchUser()
    ;(async () => {
      try {
        const status = await subscriptionsApi.status()
        setSubscriptionStatus(status)
      } catch {}
    })()
    ;(async () => {
      try {
        const me = await authApi.getMe()
        if (me.parish_id) {
          const list = await parishesApi.list()
          const active = list.find(p => p.id === me.parish_id)
          if (active) {
            setParishName(active.name)
            setSharecode(active.sharecode)
          }
        }
      } catch {}
    })()
  }, [])

  if (!user) {
    return <div>Loading...</div>
  }

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      <AppHeader
        onParishCreate={() => setShowParishSetup(true)}
        onParishSwitched={async () => {
          const me = await authApi.getMe()
          updateUser(me)
          if (me.parish_id) {
            const list = await parishesApi.list()
            const active = list.find(p => p.id === me.parish_id)
            if (active) {
              setParishName(active.name)
              setSharecode(active.sharecode)
            }
          }
        }}
        parishListRefreshKey={parishListRefreshKey}
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>
        {subscriptionStatus && (
          <NoticeBanner daysUntilExpiry={subscriptionStatus.days_until_expiry ?? undefined} />
        )}
        {user && user.password_changed_at && (() => {
          // Calculate days since last password change
          const lastChange = new Date(user.password_changed_at)
          const now = new Date()
          const daysSince = Math.floor((now.getTime() - lastChange.getTime()) / (1000 * 60 * 60 * 24))
          return (
            <PasswordReminderBanner daysSinceLastChange={daysSince} />
          )
        })()}
        {/* Spec section 10 "Admin Dashboard Page": every admin role gets registration, approvals, notes & birthdays */}
        {isAdminRole(user.role) && user.parish_id && (
          <>
            <RegistrationBlock parishName={parishName} sharecode={sharecode} />
            <div className="mt-3">
              <button
                onClick={() => setShowPdfModal(true)}
                className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                title="Create a printable flyer with your registration link and QR code"
              >
                Save Registration PDF for Printing
              </button>
            </div>
          </>
        )}
        {/* No active parish yet (brand-new Primary Admin): nothing below works without one */}
        {isAdminRole(user.role) && !user.parish_id && (
          <section className="mt-6 p-6 rounded-xl border border-yellow-300 bg-yellow-50">
            <h2 className="text-lg font-semibold mb-2">Welcome! Let's set up your parish</h2>
            <p className="text-sm text-gray-700 mb-4">
              Your dashboard fills in once your parish is set up. If you administer several parishes, you can add the others later from the Parish menu at the top.
            </p>
            {user.role === 'PRIMARY_ADMIN' ? (
              <button className="btn-primary" onClick={() => setShowParishSetup(true)}>Set up my parish</button>
            ) : (
              <p className="text-sm text-gray-700">You aren't attached to a parish. Admins are added by their parish's Primary Admin — please ask them to add you.</p>
            )}
          </section>
        )}
        {/* Admin features for all admin roles */}
        {isAdminRole(user.role) && user.parish_id && (
          <>
            {(
              <>
                <ApprovalsAccordion />
                <motion.section
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
                >
                  <h2 className="text-lg font-semibold mb-2">Notes</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    This is your scratch pad, where you can add notes, inspiration, and generally keep track of miscellaneous things...
                  </p>
                  <Notes />
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <BirthdayReminders />
                  </div>
                </motion.section>
              </>
            )}
            {/* Admin List - visible to PRIMARY_ADMIN and DESIGNATED_ADMIN */}
            {(user.role === 'PRIMARY_ADMIN' || user.role === 'DESIGNATED_ADMIN') && <AdminList {...sectionProps('admins')} />}

            {/* Teacher and Class management - visible to all admin roles */}
            <TeacherList {...sectionProps('teachers')} />
            <ClassesList {...sectionProps('classes')} />
            <StudentsList {...sectionProps('students')} />
            
            {/* Auto-registration rules: any Admin (spec). Merge: Primary Admin's menu only (spec). */}
            <AutoRegistrationRules />
            {user.role === 'PRIMARY_ADMIN' && <MergeDoubleParentTool />}
            
            {/* Messaging available to all admin roles */}
            <MessageCenter />
          </>
        )}
      </main>
      <ParishSetupModal
        open={showParishSetup}
        onClose={() => setShowParishSetup(false)}
        onCreated={async (pid) => {
          updateUser({ parish_id: pid })
          // Refresh parish info immediately
          try {
            const me = await authApi.getMe()
            updateUser(me)
            if (me.parish_id) {
              const list = await parishesApi.list()
              const active = list.find(p => p.id === me.parish_id)
              if (active) {
                setParishName(active.name)
                setSharecode(active.sharecode)
              }
            }
            // Trigger parish list refresh in header
            setParishListRefreshKey(k => k + 1)
          } catch (error) {
            console.error('Failed to refresh parish info:', error)
          }
        }}
      />
      <TOSModal
        open={showTos}
        onAccepted={async () => {
          setShowTos(false)
          const me = await authApi.getMe()
          updateUser(me)
          if (me.role === 'PRIMARY_ADMIN' && !me.parish_id) setShowParishSetup(true)
        }}
      />
      <RegistrationPdfModal open={showPdfModal} onClose={() => setShowPdfModal(false)} />
      <AppFooter />
    </div>
  )
}

export default DashboardPage
